import { storableError } from 'util/errors';
import { storedData } from 'containers/CheckoutPage/CheckoutPageSessionHelpers';
import { denormalisedResponseEntities } from 'util/data';
import { initiatePrivileged, transitionPrivileged } from 'util/api';
import { speculateTransactionSuccess } from 'containers/CheckoutPage/CheckoutPage.duck';
import { getProcess } from '../../../transactions/transaction';
import { FREE_BLOCK_BOOKING_PROCESS_ALIAS } from '../../../extensions/free-booking/transactions/transactionProcessFreeBlockBooking';
import { determineTxProcessAlias } from '../../checkout-page/utils/determineTxProcessAlias';
import getOrderParams from '../utils/getOrderParams';
import calculateSubTotal from '../utils/calculateSubTotal';
import * as log from 'util/log';

const initialState = {
  applying: false,
  applied: false,
  applyError: null,
  appliedPromoCodeData: {},
};

const APPLY_PROMO_CODE_REQUEST = 'app/promoCode/APPLY_PROMO_CODE_REQUEST';
const APPLY_PROMO_CODE_SUCCESS = 'app/promoCode/APPLY_PROMO_CODE_SUCCESS';
const APPLY_PROMO_CODE_ERROR = 'app/promoCode/APPLY_PROMO_CODE_ERROR';

const REMOVE_PROMO_CODE = 'app/promoCode/REMOVE_PROMO_CODE';
const RESET_APPLY_PROMO_CODE_STATE = 'app/promoCode/RESET_APPLY_PROMO_CODE_STATE';

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case APPLY_PROMO_CODE_REQUEST:
      return {
        ...state,
        applying: true,
        applied: false,
        applyError: null,
        appliedPromoCodeData: {},
      };
    case APPLY_PROMO_CODE_SUCCESS:
      return { ...state, applying: false, applied: true, appliedPromoCodeData: payload };
    case APPLY_PROMO_CODE_ERROR:
      return {
        ...state,
        applying: false,
        applied: false,
        applyError: payload,
        appliedPromoCodeData: {},
      };
    case REMOVE_PROMO_CODE:
      return {
        ...state,
        applied: false,
        appliedPromoCodeData: {},
      };
    case RESET_APPLY_PROMO_CODE_STATE:
      return initialState;
    default:
      return state;
  }
}

const applyRequest = () => ({ type: APPLY_PROMO_CODE_REQUEST });
const applySuccess = (payload) => ({ type: APPLY_PROMO_CODE_SUCCESS, payload });
const applyError = (e) => ({ type: APPLY_PROMO_CODE_ERROR, payload: e });

const removeAppliedCode = () => ({ type: REMOVE_PROMO_CODE });
export const resetApplyPromoCodeState = () => ({ type: RESET_APPLY_PROMO_CODE_STATE });

const speculateTransactionWithDiscount =
  (orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition) =>
  async (dispatch, getState, sdk) => {
    // If we already have a transaction ID, we should transition, not
    // initiate.
    const isTransition = !!transactionId;

    const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
    const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
    const bookingParamsMaybe = bookingDates || {};

    // Parameters only for client app's server
    const orderData = deliveryMethod ? { deliveryMethod } : {};
    const { ticketOption, isFreePromoCode } = otherOrderParams;

    // Parameters for Marketplace API
    const transitionParams = {
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...otherOrderParams,
      cardToken: 'CheckoutPage_speculative_card_token',
    };

    const bodyParams = isTransition
      ? {
          id: transactionId,
          transition: transitionName,
          params: transitionParams,
        }
      : {
          processAlias: determineTxProcessAlias({
            ticketOption,
            processAlias,
            // No listing here; the caller resolved its free flag into processAlias.
            isFreeBlockBooking: processAlias === FREE_BLOCK_BOOKING_PROCESS_ALIAS,
            isFreePromoCode,
          }),
          transition: transitionName,
          params: transitionParams,
        };

    const queryParams = {
      include: ['booking', 'provider'],
      expand: true,
    };

    const handleSuccess = (response) => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the speculate response');
      }
      const tx = entities[0];
      dispatch(speculateTransactionSuccess(tx));
      return tx;
    };

    if (isTransition && isPrivilegedTransition) {
      // transition privileged
      const response = await transitionPrivileged({
        isSpeculative: true,
        orderData,
        bodyParams,
        queryParams,
      });
      return handleSuccess(response);
    }
    if (isTransition) {
      // transition non-privileged
      const response = await sdk.transactions.transitionSpeculative(bodyParams, queryParams);
      return handleSuccess(response);
    }
    if (isPrivilegedTransition) {
      // initiate privileged
      const response = await initiatePrivileged({
        isSpeculative: true,
        orderData,
        bodyParams,
        queryParams,
      });
      return handleSuccess(response);
    }
    // initiate non-privileged
    const response = await sdk.transactions.initiateSpeculative(bodyParams, queryParams);
    return handleSuccess(response);
  };

// Thunk for applying promo code
export const applyPromoCode = (params) => async (dispatch, getState) => {
  dispatch(applyRequest());
  try {
    const { promoCode, config, otherParams = {} } = params;
    const pageData = storedData('CheckoutPage');
    const { listing, transaction: tx } = pageData;
    const processAlias = listing.attributes.publicData?.transactionProcessAlias;
    const txId = tx ? tx.id : null;
    const processName =
      tx?.attributes?.processName ||
      listing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
    const process = processName ? getProcess(processName) : null;
    const requestTransition = process.getRequestTransition(tx);
    const isPrivileged = process.isPrivileged(requestTransition);
    const orderParams = getOrderParams(pageData, {}, {}, config, otherParams);
    const transactionWithDiscount = await dispatch(
      speculateTransactionWithDiscount(
        { ...orderParams, promoCode },
        processAlias,
        txId,
        requestTransition,
        isPrivileged
      )
    );
    const { lineItems } = transactionWithDiscount.attributes;
    dispatch(
      applySuccess({
        promoCode,
        discountedTxLineItems: lineItems,
      })
    );
    if (typeof window !== 'undefined' && window.posthog) {
      const currentUser = getState().user.currentUser;
      const currency = listing.attributes.price?.currency;
      const basketTotal = calculateSubTotal({ lineItems, currency });

      window.posthog.capture('promo code applied', {
        user_id: currentUser?.id?.uuid,
        listing_id: listing.id?.uuid,
        provider_id: listing.author?.id?.uuid,
        promo_code: promoCode,
        amount: Number(basketTotal.amount),
        currency: basketTotal.currency,
      });
    }
    return true;
  } catch (e) {
    log.error(e, 'apply-promo-code-failed', { promoCode: params.promoCode });
    const error = storableError(e);
    dispatch(applyError(error));
    return false;
  }
};

export const handleRemoveAppliedCode = (config, otherParams = {}) => async (dispatch) => {
  try {
    dispatch(removeAppliedCode());
    const pageData = storedData('CheckoutPage');
    const { listing, transaction: tx } = pageData;
    const processAlias = listing.attributes.publicData?.transactionProcessAlias;
    const txId = tx ? tx.id : null;
    const processName =
      tx?.attributes?.processName ||
      listing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
    const process = processName ? getProcess(processName) : null;
    const requestTransition = process.getRequestTransition(tx);
    const isPrivileged = process.isPrivileged(requestTransition);
    const orderParams = getOrderParams(pageData, {}, {}, config, otherParams);
    await dispatch(
      speculateTransactionWithDiscount(
        orderParams,
        processAlias,
        txId,
        requestTransition,
        isPrivileged
      )
    );
  } catch (e) {
    const error = storableError(e);
    // Log error but don't show to user as removing promo code is not expected to fail
    console.error(error);
  }
};

export const isApplying = (state) => state.applyPromoCode.applying;
export const isApplied = (state) => state.applyPromoCode.applied;
export const applyPromoCodeError = (state) => state.applyPromoCode.applyError;
export const appliedPromoCode = (state) => state.applyPromoCode.appliedPromoCodeData;
